/* formato.ts — cómo se escriben las cifras y las fechas en Vigía.
 *
 * REGLA DURA DEL PRODUCTO: el dinero se dice en «millones» y «billones» ESCRITOS COMPLETOS.
 * Prohibido `mM`, `mil M`, `bill.`, o una `M`/`B` suelta: quien lee esto es un ciudadano o una
 * empresa pequeña, y esas abreviaturas se confunden entre sí. Locale es-CO: coma decimal y punto
 * de miles.
 *
 * Y cuando no hay dato se DICE («Sin presupuesto publicado»), no se muestra 0 ni un guion: un
 * cero es una afirmación, y casi siempre es falsa.
 */

import type { CambioProceso } from './tipos'

const CO = 'es-CO'

const num = (n: number, decimales = 0) =>
  n.toLocaleString(CO, { minimumFractionDigits: decimales, maximumFractionDigits: decimales })

/**
 * Dinero en pesos → «$1.265 millones» · «$2,23 billones» · «$840.000».
 *
 * El cero se trata como AUSENCIA, no como cifra: en esta fuente un presupuesto en 0 significa
 * «no diligenciado», y escribir «$0» sería afirmar algo falso sobre el proceso. Mismo criterio
 * que `fM()` en el panel anterior.
 */
export function pesos(v: number | null | undefined, siFalta = 'Sin presupuesto publicado'): string {
  if (v === null || v === undefined || !Number.isFinite(v) || v <= 0) return siFalta
  if (v >= 1e12) return `$${num(v / 1e12, 2)} billones`
  if (v >= 1e6) {
    const millones = Math.round(v / 1e6)
    // «$1 millones» no lo escribe nadie.
    return `$${num(millones)} ${millones === 1 ? 'millón' : 'millones'}`
  }
  return `$${num(v)}`
}

/**
 * Solo un `http(s)` llega a un `href`. Los datos vienen de la API de SECOP; esto es defensa en
 * profundidad contra esquemas ejecutables (`javascript:`) que se colaran en un campo de URL.
 */
export function urlSegura(u: string | null | undefined): string | null {
  if (!u) return null
  try {
    const x = new URL(u)
    return x.protocol === 'https:' || x.protocol === 'http:' ? x.href : null
  } catch {
    return null
  }
}

/** Igual que `pesos`, pero para columnas estrechas de tabla: «$1.265 millones» sigue completo
 *  porque la regla no admite abreviar; lo que se recorta es el resto del diseño, no la cifra. */
export const pesosCorto = pesos

/** Entero con punto de miles: «9.904». */
export function entero(v: number | null | undefined, siFalta = '—'): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return siFalta
  return num(v)
}

/** Número con decimales: «10,1». Coma decimal, que es la de aquí — `toFixed()` escribe «10.1»,
 *  y en es-CO un punto es separador de miles: «10.1» se lee como diez mil y algo. */
export function decimal(v: number | null | undefined, decimales = 1, siFalta = '—'): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return siFalta
  return num(v, decimales)
}

/** Porcentaje con coma decimal: «98,2 %». `fraccion` indica si viene en 0-1 o ya en 0-100.
 *
 *  El espacio antes del signo es U+00A0, DURO, y eso no es cosmética: con el espacio normal el
 *  navegador lo lee como frontera de palabra y puede partir por ahí. En la ficha pasó —«100 %» a
 *  36 px dentro de un flex que se quedó sin 0,0 px— y lo que quedó en pantalla fue «100» y, un
 *  renglón más abajo, «%». La cifra dejó de leerse como una cifra.
 *
 *  No se puede depurar mirando el código: los dos espacios se escriben igual. Por eso va el escape
 *  explícito y no un carácter invisible pegado. */
export function porcentaje(
  v: number | null | undefined,
  { fraccion = false, decimales = 0, siFalta = '—' } = {},
): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return siFalta
  return `${num(fraccion ? v * 100 : v, decimales)}\u00a0%`
}

/**
 * ¿El texto viene GRITADO? En SECOP muchas entidades escriben el objeto entero en mayúsculas y
 * otras no, así que la misma pantalla mezcla las dos cosas.
 *
 * No se transforma el texto: se detecta para poder componerlo de otra manera. Pasarlo a minúsculas
 * sería inventar dónde van las mayúsculas —«Localidad de Kennedy» quedaría «localidad de kennedy»—
 * y eso es perder un dato que sí está en la fuente. Lo que sí se puede es dejar de maquetarlo como
 * un titular normal: 29 px en negrita y seis renglones de mayúsculas son un muro.
 */
export function esGritado(texto: string | null | undefined): boolean {
  const letras = (texto ?? '').replace(/[^\p{L}]/gu, '')
  if (letras.length < 12) return false
  const mayus = letras.replace(/[^\p{Lu}]/gu, '').length
  return mayus / letras.length >= 0.8
}

/**
 * Siglas y códigos que se quedan en MAYÚSCULAS al normalizar un objeto.
 *
 * Cerrada y corta a propósito: solo lo que aparece de verdad en los objetos de SECOP. Ampliarla es
 * barato; adivinar por forma («toda palabra de 3-4 letras es sigla») convertiría «PARA», «CON» o
 * «VÍAS» en siglas.
 */
const SIGLAS = new Set([
  'SECOP', 'RUP', 'RUT', 'NIT', 'IVA', 'UNSPSC', 'PAE', 'ESE', 'IPS', 'EPS', 'ARL', 'SGR', 'SGP',
  'DANE', 'DIAN', 'SENA', 'ICBF', 'INVIAS', 'ANI', 'UAE', 'EOT', 'POT', 'PGN', 'CDP', 'RP', 'AIU',
  'IPC', 'SMMLV', 'TIC', 'PQRS', 'SIG', 'ISO', 'NTC', 'RETIE', 'RETILAP', 'GPS', 'LED', 'PVC',
  'PDET', 'ZOMAC', 'OCAD', 'PQR', 'SST', 'EPP', 'VIS', 'VIP', 'PMU', 'CAI', 'UPZ', 'POAI', 'PDA',
  // Códigos de clase/categoría que aparecen en objetos de compra técnica («CLASE I CATEGORÍA IA»).
  'IA', 'IB', 'IIA', 'IIB',
])
const ROMANOS = /^[IVX]{1,5}$/

const sinTildes = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/**
 * El objeto del proceso, legible.
 *
 * En SECOP cada entidad escribe como quiere: unas ponen el objeto entero en MAYÚSCULAS y otras en
 * frase, así que la misma lista mezcla las dos cosas y las gritadas se leen como un muro. Aquí se
 * normalizan a frase — pero con cuidado, porque bajar todo a minúsculas PIERDE información:
 *
 *  · las SIGLAS y los códigos vuelven a subir (`SECOP`, `FDLK-LP-004-2026`, `IA`, `II`);
 *  · y `propios` deja recuperar los nombres propios que sí conocemos por los DATOS —el municipio y
 *    el departamento de la entidad—, que es de donde salen «Chocontá», «Cundinamarca» o «Albania»
 *    sin necesidad de una lista de los 1.100 municipios del país.
 *
 * ⚠ Lo que NO debe pasarse como `propios` es el NOMBRE de la entidad. Se probó y produce falsos
 * positivos: «ALCALDÍA MAYOR DE BOGOTÁ» convierte cualquier «mayor» del objeto en «Mayor», y
 * «adulto Mayor» se lee peor que el original. Solo topónimos.
 *
 * Lo que NO se puede recuperar es un nombre propio que no esté en ninguna de esas fuentes
 * («Usaquén» en un objeto de una entidad distrital). Por eso quien lo muestre debe conservar el
 * texto original a mano —en el `title`, por ejemplo—: esto mejora la lectura, no reemplaza al dato.
 */
export function normalizarObjeto(texto: string | null | undefined, propios: (string | null | undefined)[] = []): string {
  const original = (texto ?? '').trim()
  if (!esGritado(original)) return original

  const conocidos = new Set<string>()
  for (const p of propios) {
    for (const palabra of (p ?? '').split(/[^\p{L}\p{N}]+/u)) {
      if (palabra.length >= 3) conocidos.add(sinTildes(palabra))
    }
  }

  let empezar = true
  return original
    .split(/(\s+)/)
    .map((token) => {
      if (/^\s+$/.test(token)) return token
      const nucleo = token.replace(/[^\p{L}\p{N}-]/gu, '')
      const suelta = () => {
        // Un token con dígitos o guiones es un código: se deja intacto.
        if (/\d/.test(nucleo) || ROMANOS.test(nucleo)) return true
        return SIGLAS.has(nucleo.toUpperCase())
      }
      let salida: string
      if (suelta()) {
        salida = token
      } else {
        const bajo = token.toLocaleLowerCase('es')
        const propio = conocidos.has(sinTildes(nucleo))
        salida = empezar || propio ? capitalizar(bajo) : bajo
      }
      // Una frase nueva empieza tras punto, interrogación o exclamación de cierre.
      empezar = /[.?!:]$/.test(token.trim())
      return salida
    })
    .join('')
}

/**
 * Palabras que se quedan en minúscula DENTRO de un nombre propio. Solo preposiciones, artículos y
 * conjunciones: en «Alcaldía Mayor de Bogotá» lo único que baja es el «de».
 */
const ENLACES = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'y', 'e', 'o', 'u', 'en', 'para', 'por', 'a', 'al', 'con',
  'sin', 'sobre', 'ante',
])

/**
 * Siglas de entidades públicas colombianas, que aparecen dentro del NOMBRE y no dentro del objeto
 * («… DE CUNDINAMARCA - CAR», «EMPRESA DE ACUEDUCTO … EAAB»).
 *
 * Va aparte de `SIGLAS` porque son dos vocabularios distintos: aquí no sirve de nada `IVA` ni
 * `RETIE`, y allí no sirve `IDRD`. Y no hay heurística que valga: en un texto TODO en mayúsculas,
 * `CAR` es indistinguible de una palabra, así que o está en la lista o baja a caja de nombre.
 *
 * Lista cerrada y ampliable. Lo que no esté se compone como palabra («- Car»): es una pérdida
 * pequeña y VISIBLE —se ve en pantalla y se corrige añadiendo una línea aquí—, no un dato dañado.
 */
const SIGLAS_ENTIDAD = new Set([
  'CAR', 'ESP', 'ESE', 'IPS', 'EPS', 'SA', 'SAS', 'LTDA', 'EICE', 'EU',
  'IDU', 'IDRD', 'IDIGER', 'IDPC', 'IDT', 'EAAB', 'UAESP', 'UAECOB', 'ETB', 'EPM', 'EMCALI',
  'SDS', 'SDA', 'SDH', 'SDP', 'SED', 'UAEAC', 'UARIV', 'UNGRD', 'DPS', 'DADEP', 'DASCD',
  'ICA', 'INVIMA', 'INPEC', 'IGAC', 'IDEAM', 'ESAP', 'UNAD', 'UPTC', 'UIS', 'UPME', 'ITM',
  'ANLA', 'ANH', 'ANM', 'ANT', 'ADR', 'ART', 'ANDJE', 'ANSV', 'AEROCIVIL',
  'ICETEX', 'MINTIC', 'CVC', 'CRC', 'CDMB', 'CAM', 'CRA', 'CVS', 'CSB', 'CORPOBOYACA',
])

/**
 * El nombre de una entidad o de un proveedor, legible.
 *
 * Distinto de `normalizarObjeto` y por eso una función aparte. Un OBJETO es una frase —«adquisición
 * de vehículo»— y se compone en minúscula salvo lo que sepamos que es propio. Un NOMBRE es propio
 * entero: «ALCALDIA DE CHOCONTA» va a «Alcaldía de Chocontá» con TODAS las palabras en alta menos
 * los enlaces. Pasar un nombre por `normalizarObjeto` daría «Alcaldia de choconta», que es peor que
 * dejarlo gritado. El diseño los escribe siempre así («Alcaldía de Rionegro», «Federación Nacional
 * de Cafeteros»), nunca en mayúsculas.
 *
 * Lo que NO hace y conviene saber: no devuelve las tildes que la fuente no trae. En SECOP muchas
 * entidades están escritas sin ellas («ALCALDIA DE CHOCONTA»), y aquí solo se cambia la caja —
 * inventar tildes sería inventar el dato. Quien lo muestre conserva el original en el `title`.
 */
export function normalizarNombre(texto: string | null | undefined): string {
  const original = (texto ?? '').trim()
  if (!esGritado(original)) return original
  let primera = true
  return original
    .split(/(\s+)/)
    .map((token) => {
      if (/^\s+$/.test(token)) return token
      const nucleo = token.replace(/[^\p{L}\p{N}-]/gu, '')
      /* Un código, un número romano, una sigla conocida o una sigla con puntos se quedan como
       * están: bajarlos los haría irreconocibles y dejarían de buscarse.
       *
       * Las dos alternativas del patrón son porque la fuente escribe la última letra con punto y
       * sin él, y las dos formas son la misma sigla: «E.S.P.» y «D.C». Con solo la primera, «D.C»
       * salía «D.c», que es exactamente el tipo de detalle que delata que nadie miró la pantalla. */
      const letras = token.replace(/[^\p{L}.]/gu, '')
      const conPuntos = /^(?:\p{Lu}\.){2,}$/u.test(letras) || /^(?:\p{Lu}\.)+\p{Lu}$/u.test(letras)
      const arriba = nucleo.toUpperCase()
      if (/\d/.test(nucleo) || ROMANOS.test(nucleo) || conPuntos
          || SIGLAS.has(arriba) || SIGLAS_ENTIDAD.has(arriba)) {
        primera = false
        return token
      }
      const bajo = token.toLocaleLowerCase('es')
      const enlace = !primera && ENLACES.has(nucleo.toLocaleLowerCase('es'))
      primera = false
      return enlace ? bajo : capitalizar(bajo)
    })
    .join('')
}

/** Pone en mayúscula la primera LETRA, saltándose comillas, paréntesis y guiones de apertura. */
function capitalizar(s: string): string {
  const i = s.search(/\p{L}/u)
  if (i < 0) return s
  return s.slice(0, i) + s[i]!.toLocaleUpperCase('es') + s.slice(i + 1)
}

/** Fecha larga en español: «15 de agosto de 2026». */
export function fecha(iso: string | null | undefined, siFalta = 'Sin fecha'): string {
  const d = aFecha(iso)
  if (!d) return siFalta
  return d.toLocaleDateString(CO, { day: 'numeric', month: 'long', year: 'numeric' })
}

/** Fecha corta para columnas: «15 ago». */
export function fechaCorta(iso: string | null | undefined, siFalta = '—'): string {
  const d = aFecha(iso)
  if (!d) return siFalta
  return d.toLocaleDateString(CO, { day: 'numeric', month: 'short' })
}

/**
 * Días que faltan para una fecha, contados por CALENDARIO. Negativo si ya pasó.
 *
 * Se comparan dos medianoches locales, no milisegundos transcurridos. Con el cálculo por tiempo
 * transcurrido, un proceso que cierra hoy a las 23:59 sale como «1 día» a las 10 de la mañana:
 * `ceil(13,98 h / 24 h) = 1`. Y «te queda un día» cuando quedan catorce horas es justo el error
 * que hace perder una oferta — «¿Te alcanza el tiempo?» es una de las preguntas de la ficha.
 *
 * Restar medianoches y redondear también absorbe los días de 23 o 25 horas del horario de verano:
 * Colombia no lo tiene, pero el navegador de quien mira puede estar en cualquier parte.
 */
export function diasHasta(iso: string | null | undefined): number | null {
  const d = aFecha(iso)
  if (!d) return null
  const medianoche = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  return Math.round((medianoche(d) - medianoche(new Date())) / 86_400_000)
}

/** «5 días» · «hoy» · «cerrado». Lo que ve quien decide si le alcanza el tiempo. */
/**
 * ¿El plazo de este proceso se dice como una CUENTA ATRÁS, y admite «cierra en» delante?
 *
 * Existe porque el móvil escribe «cierra en 5 días» y `plazo()` no siempre devuelve una cuenta: en
 * los bordes ya trae su propio verbo («Cierra hoy») o niega el plazo («Cerrado», «Sin fecha de
 * cierre»). Poner el prefijo sin mirar el caso producía «cierra en Cierra hoy» — y lo producía de
 * verdad, porque la primera versión lo puso en un `::before` de CSS, que no puede consultar el
 * dato. Se decide por los DÍAS, no leyendo la cadena que devuelve `plazo()`: una comprobación sobre
 * el texto se rompería en silencio al cambiar una palabra.
 */
export function plazoEsCuenta(iso: string | null | undefined): boolean {
  const d = diasHasta(iso)
  return d !== null && d >= 1
}

export function plazo(iso: string | null | undefined): string {
  const d = diasHasta(iso)
  if (d === null) return 'Sin fecha de cierre'
  if (d < 0) return 'Cerrado'
  if (d === 0) return 'Cierra hoy'
  if (d === 1) return '1 día'
  return `${num(d)} días`
}

/**
 * El cubo guarda las fechas como texto naive («2026-07-23 00:00:00»), sin zona. Interpretarlas
 * como UTC adelantaría el cierre cinco horas en Colombia y un proceso que cierra hoy se vería
 * como cerrado. Se normaliza a hora local reemplazando el espacio por «T» y sin sufijo Z.
 */
function aFecha(iso: string | null | undefined): Date | null {
  if (!iso) return null
  /* ⚠ Una fecha SIN hora («2026-07-19») no basta con dejarla pasar: el estándar manda que
   * `new Date('2026-07-19')` se interprete como UTC —y `'2026-07-19T00:00:00'`, como LOCAL—, así
   * que en Colombia la primera retrocede un día y «al 19 de julio» se imprime «al 18 de julio».
   * Pasó en la fecha de corte de la pantalla de ingreso, que es una cadena de solo fecha.
   *
   * Por eso se completa la hora en vez de confiar en la forma: las tres variantes que llegan de
   * verdad —«2026-07-19», «2026-07-19 00:00:00» y «2026-07-19T00:00:00»— acaban en la misma. */
  const s = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso.replace(' ', 'T')
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Qué cambió del proceso, en una frase, a partir de `Proceso.cambios`.
 *
 * ⚠ La honestidad de esto está en lo que NO dice. `cambios` es un DIFF ENTRE SNAPSHOTS del sync:
 * sabe que el cierre se movió del 12 al 19 de agosto —dato oficial, comparando dos corridas— pero
 * NO sabe quién lo movió. Escribir «la adenda 2 cambió el cierre» sería una atribución que no
 * consta en ningún dato: puede haber varias adendas, ninguna, o el cambio puede venir de una
 * corrección de la entidad. Por eso las frases van en impersonal.
 *
 * Y por eso tampoco dice CUÁNDO cambió: la granularidad es la del sync, no la del hecho.
 */
export function frasesDeCambios(cambios: CambioProceso[] | null | undefined): string[] {
  const salida: string[] = []
  for (const c of cambios ?? []) {
    if (c.campo === 'fecha_cierre' && c.antes && c.ahora) {
      salida.push(`el cierre pasó del ${fechaCorta(String(c.antes))} al ${fechaCorta(String(c.ahora))}`)
    } else if (c.campo === 'precio_base' && typeof c.ahora === 'number') {
      // El presupuesto SE DICE ENTERO en las dos puntas: «de $800 millones a $842 millones». Un
      // «subió un 5 %» obligaría a quien lee a reconstruir la cifra que le importa.
      salida.push(`el presupuesto pasó de ${pesos(Number(c.antes))} a ${pesos(c.ahora)}`)
    }
  }
  return salida
}

/** Etiqueta legible de cada banda de veredicto. El color NUNCA informa solo: esto va siempre. */
/**
 * Cuánto hace que pasó algo, en epoch de SEGUNDOS (que es lo que manda el servidor).
 *
 * Redondeado a propósito: la fecha exacta de un análisis no le sirve a nadie para decidir, y «hace
 * 3 días» se lee de un vistazo mientras que «17/08/2026 14:32» hay que restarlo mentalmente. Los
 * relojes desincronizados (o un servidor en otro huso) darían negativo: eso cae en «hace un
 * momento» en vez de en un «hace -2 horas» que parecería un error de la aplicación.
 */
export function haceCuanto(epochSegundos: number | null | undefined, siFalta = ''): string {
  if (!epochSegundos) return siFalta
  const seg = Math.floor(Date.now() / 1000) - epochSegundos
  if (seg < 90) return 'hace un momento'
  const min = Math.round(seg / 60)
  if (min < 60) return `hace ${min} ${min === 1 ? 'minuto' : 'minutos'}`
  const h = Math.round(min / 60)
  if (h < 24) return `hace ${h} ${h === 1 ? 'hora' : 'horas'}`
  const d = Math.round(h / 24)
  if (d === 1) return 'ayer'
  if (d < 30) return `hace ${d} días`
  const m = Math.round(d / 30)
  if (m < 12) return `hace ${m} ${m === 1 ? 'mes' : 'meses'}`
  const a = Math.round(m / 12)
  return `hace ${a} ${a === 1 ? 'año' : 'años'}`
}

export const ETIQUETA_BANDA = {
  compite: 'Compite',
  cautela: 'Compite con cautela',
  piensalo: 'Piénsalo dos veces',
  mejor_no: 'Mejor no',
} as const

/** Etiqueta de la apertura del comprador. Alto es bueno. */
export const ETIQUETA_APERTURA = {
  abierto: 'Reparte entre proveedores',
  medio: 'Competencia irregular',
  cerrado: 'Casi siempre el mismo proveedor',
  sin_datos: 'Sin historial',
} as const
